import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/router";

import { constants, useWindowSize } from "@newsela/angelou";

import { LessonSparkAttachment } from "@/bundles/types/LessonSparkAttachment";

import useLangstonAttachmentsQuery from "@/bundles/hooks/useLangstonAttachmentsQuery";
import { useBundleDataContext } from "@/bundles/providers/useBundleDataContext";

interface AttachmentsContextValue {
  attachmentData: LessonSparkAttachment[] | unknown;
  hasNoAttachments: boolean;
  isAttachmentsFetching: boolean;
  isResourcePanelOpen: boolean;
  setIsResourcePanelOpen: (val: boolean) => void;
}

const AttachmentsContext = createContext<AttachmentsContextValue | undefined>(
  undefined,
);

export const AttachmentsContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { content, contentLoading, currentContentId } = useBundleDataContext();
  const router = useRouter();
  const { attachment } = router.query as { attachment?: string };
  const { width: windowWidth } = useWindowSize();
  const {
    breakpoints: {
      Breakpoints: { md },
    },
  } = constants;

  // Detect desktop viewport (≥1024px / md breakpoint)
  // This matches where panel switches from drawer to sidebar
  const isDesktop = windowWidth >= md;

  const attachmentIds = useMemo(() => {
    return !contentLoading && content && content !== undefined
      ? content.page_viewmodel?.documents[0]?.attachment_ids || []
      : [];
  }, [content, contentLoading]);

  // Resource panel is hidden completely when there are no attachments.
  const hasNoAttachments = !contentLoading && attachmentIds.length === 0;

  // Resource panel opens initially only for deep links (attachment param)
  // Desktop auto-open is handled by useEffect after confirming attachments exist
  const [isResourcePanelOpen, setIsResourcePanelOpen] = useState<boolean>(
    () => {
      return !!attachment;
    },
  );

  // Tracks whether we've already auto-opened for the current content.
  // Prevents the auto-open effect from re-firing (and re-opening) after
  // the user explicitly closes the panel (e.g. clearing ?attachment from URL
  // triggers the effect again via the `attachment` dep, but we should respect
  // the user's intent to close).
  const hasAutoOpenedRef = useRef(false);

  const { data: attachmentData, isFetching: isAttachmentsFetching } =
    useLangstonAttachmentsQuery({
      attachmentIds,
      enabled: isResourcePanelOpen,
    });

  useEffect(() => {
    setIsResourcePanelOpen(false);
    hasAutoOpenedRef.current = false;
  }, [currentContentId]);

  useEffect(() => {
    if (hasAutoOpenedRef.current) return;

    if (attachment) {
      // Open the panel immediately so the query becomes enabled.
      // Don't wait for attachmentData, it can't load until isResourcePanelOpen is true.
      setIsResourcePanelOpen(true);
      hasAutoOpenedRef.current = true;
      return;
    }
    if (isDesktop && !contentLoading && !hasNoAttachments) {
      // Desktop default: open as soon as we know attachments exist.
      setIsResourcePanelOpen(true);
      hasAutoOpenedRef.current = true;
    }
  }, [
    currentContentId,
    contentLoading,
    hasNoAttachments,
    isDesktop,
    attachment,
  ]);

  // Memoize context value so that children don’t re-render unnecessarily
  const value = useMemo<AttachmentsContextValue>(
    () => ({
      attachmentData,
      hasNoAttachments,
      isAttachmentsFetching,
      isResourcePanelOpen,
      setIsResourcePanelOpen,
    }),
    [
      attachmentData,
      hasNoAttachments,
      isAttachmentsFetching,
      isResourcePanelOpen,
      setIsResourcePanelOpen,
    ],
  );

  return (
    <AttachmentsContext.Provider value={value}>
      {children}
    </AttachmentsContext.Provider>
  );
};

export const useAttachmentsContext = () => {
  const context = useContext(AttachmentsContext);
  if (!context) {
    throw new Error(
      "useAttachmentsContext must be used within a AttachmentsContextProvider",
    );
  }
  return context;
};
